import { ReadonlyCollection } from "@ioip/core/lib";
import { Guid, IDictionary, isNullOrEmpty } from "@ioip/core/lib/utilities";
import { List } from "linq.ts";
import { IObject } from "../../portal/types";
import { ControlType } from '../../types/ControlType';
import CCheckBox from "./CCheckBox";
import CComboBox from "./CComboBox";
import CControl from "./CControl";
import CDataTable from "./CDataTable";
import CDatePicker from "./CDatePicker";
import CField from "./CField";
import CImage from "./CImage";
import CLabel from "./CLabel";
import CRow from "./CRow";
import CSubtitle from "./CSubtitle";
import CTextField from "./CTextField";
import CTitle from "./CTitle";
import { IFieldSchema } from "./IFieldSchema";
import { IFieldValue } from "./IFieldValue";
import { IFormSchema } from "./IFormSchema";
import { JSEngine } from "./JSEngine";
import JSGroupedDataGrid from "./JSGroupedDataGrid";
import { JSPage } from "./JSPage";
import CCard from "./CCard";

export interface ICFormProps {
    object: IObject<IFormSchema>;
    engine: JSEngine;
    page: JSPage;
    isRuntime: boolean;
    itemId?: string | number;
}

export class CForm {

    #props: ICFormProps;
    #fields: ReadonlyCollection<CField> = null;
    #rows: ReadonlyCollection<CRow> = null;
    #disableUpdateCallback: boolean = false;
    #invokeRender: () => void;
    #readonly: boolean = false;
    private __key: string = null;

    constructor(props: ICFormProps) {
        this.#props = props;
        if (props.isRuntime)
            this.#initialize();
    }

    get objectId(): string {
        return this.#props.object.objectId;
    }

    get name(): string {
        return this.#props.object.name;
    }

    get title(): string {
        return this.#props.object.schema.title;
    }

    get key(): string {
        return this.__key;
    }

    get page(): JSPage {
        return this.#props.page;
    }

    get description(): string {
        return this.#props.object.description || '';
    }

    get fields(): ReadonlyCollection<CControl> {
        return this.#fields.where(x => x.controlType != ControlType.row).cast<CControl>();
    }

    ofType(type: any): ReadonlyCollection<CControl> {
        return this.fields.where(x => x instanceof type);
    }

    get commandBar(): any {
        return this.#props.object.schema.permissions || [];
    }

    get rows(): ReadonlyCollection<CRow> {
        if (this.#rows == null) {
            const rows = this.#fields.where(x => x.controlType == ControlType.row).cast<CRow>().orderBy(o => Number(o.order || 0)).toArray();
            this.#rows = new ReadonlyCollection<CRow>(rows);
        }
        return this.#rows;
    }

    get readonly(): boolean {
        return this.#readonly;
    }

    set readonly(value: boolean) {
        this.#readonly = value;
        this.refresh();
    }

    #initialize = () => {

        const fields = new List<IFieldSchema>(this.#props.object.schema.fields || []).Select(schema => {
            switch (schema.controlType) {

                case ControlType.row:
                    return new CRow(this, schema);

                case ControlType.textField:
                case ControlType.numericTextField:
                    return new CTextField({ form: this, schema });

                case ControlType.checkBox:
                    return new CCheckBox({ form: this, schema });

                case ControlType.countrySelector:
                case ControlType.departmentSelector:
                case ControlType.citySelector:
                case ControlType.userSelector:
                case ControlType.sectionSelector:
                case ControlType.comboBox:
                    const dataSource = this.#props.page != null
                        ? this.#props.page.getDataSources(Guid.parse(schema.id))
                        : null;
                    return new CComboBox({ form: this, schema, dataSource });

                case ControlType.dateTimePicker:
                    return new CDatePicker({ form: this, schema });

                case ControlType.dataTable:
                case ControlType.entityDataTable:
                    return new CDataTable({ engine: this.#props.engine, form: this, schema });

                case ControlType.label:
                    return new CLabel({ form: this, schema });

                case ControlType.image:
                    return new CImage({ form: this, schema });

                case ControlType.subtitle:
                    return new CSubtitle({ form: this, schema });

                case ControlType.title:
                    return new CTitle({ form: this, schema });

                case ControlType.card:
                    return new CCard({ form: this, schema });

                case ControlType.groupedDataGrid:
                    return new JSGroupedDataGrid({ form: this, schema });

                default: return new CControl({
                    form: this,
                    schema
                })
            }
        }).ToArray();

        this.#fields = new ReadonlyCollection<CField>(fields);

    }

    /**
     * Obtiene los valores de los campos del formulario
     */
    get values(): IDictionary<IFieldValue> {
        const controls = this.#fields.where(x => x.controlType != ControlType.row).toArray();
        const result: IDictionary<IFieldValue> = {};
        for (let i = 0; i < controls.length; i++) {
            const control = controls[ i ];
            if (control instanceof CDataTable) {
                result[ control.id.toString() ] = { children: control.cast<CControl>().value as any };
            } else {
                result[ control.id.toString() ] = { value: control.cast<CDataTable>().value as any };
            }
        }
        return result;
    }

    /**
     * Establece los valores de los campos del formulario
     */
    set values(values: IDictionary<IFieldValue>) {

        if (values == null)
            return;

        this.#disableUpdateCallback = true;
        this.__key = values[ '__key__' ] as any;

        for (const key in values) {
            if (Object.prototype.hasOwnProperty.call(values, key)) {

                const value = values[ key ];
                const field = this.#fields.firstOrDefault(x => x.id.toString() == key);
                if (field != null) {

                    // Verificar si el campo es un DataTable y el valor es un array
                    const isDataTable = field instanceof CDataTable;
                    const isValueArray = value?.children instanceof Array;

                    // Verificar que todos los registros del DataTable tengan la propiedad __key__
                    if (isDataTable && isValueArray) {
                        value?.children.forEach((x: any) => x.__key__ = x.__key__ ?? Guid.newGuid().toString());
                    }

                    // Establecer valor al control
                    field.cast<CControl>().value = isDataTable ? value.children : value.value;

                }
            }
        }

        this.#disableUpdateCallback = false;
        this.refresh();
    }

    getField(nameOrId: string | Guid): CControl {
        const fields = this.#fields.where(x => x.controlType != ControlType.row).cast<CControl>();
        if (nameOrId instanceof Guid) {
            const id = nameOrId.toString();
            return fields.firstOrDefault(x => x.id.toString() == id);
        }
        return fields.firstOrDefault(x => x.name == nameOrId);
    }

    refresh(): void {
        if (!this.#disableUpdateCallback && typeof this.#invokeRender === 'function')
            this.#invokeRender?.();
    }

    set onInvokeRender(value: () => void) {
        this.#invokeRender = value;
    }

    validate(): boolean {
        const errors = this.fields.where(x => !x.validate()).toArray();
        return errors.length === 0;
    }

    async saveV1Async(): Promise<void> {

        const removeKeys = (object: IDictionary<any>) => {
            for (const key in object) {
                delete object[ '__key__' ];
                if (Object.prototype.hasOwnProperty.call(object, key)) {
                    if (object[ key ] instanceof Array) {
                        for (let i = 0; i < object[ key ].length; i++) {
                            object[ key ][ i ] = removeKeys(object[ key ][ i ]);
                        }
                    }
                }
            }
            return object;
        }
        const values = removeKeys({ ...this.values });

        // Verificar si existen cambios
        const isUpdate = this.#props.itemId != null && !isNullOrEmpty(String(this.#props.itemId));
        if (isUpdate)
            await this.#props.engine.updateV1FormDataAsync(this.objectId, this.#props.itemId, values);
        else
            await this.#props.engine.insertV1FormDataAsync(this.objectId, values);

    }

    async saveV2Async(): Promise<void> {

        const removeKeys = (object: IDictionary<IFieldValue>) => {
            for (const key in object) {
                delete object[ '__key__' ];
                if (Object.prototype.hasOwnProperty.call(object, key)) {
                    const value = object[ key ];
                    if (value?.children instanceof Array) {
                        for (let i = 0; i < value.children.length; i++) {
                            value.children[ i ] = removeKeys(value.children[ i ]);
                        }
                    }
                }
            }
            return object;
        }

        const values = removeKeys({ ...this.values });

        // Verificar si existen cambios
        const isUpdate = this.#props.itemId != null && !isNullOrEmpty(String(this.#props.itemId));
        if (isUpdate)
            await this.#props.engine.updateV2FormDataAsync(this.objectId, this.#props.itemId, values);
        else
            await this.#props.engine.insertV2FormDataAsync(this.objectId, values);

    }

}
